import { Button } from "components/DesignSystem/Button/Button";
import {
  Combobox,
  OptionData,
} from "components/DesignSystem/Combobox/Combobox";
import { DateInput } from "components/DesignSystem/DateInput/DateInput";
import Modal from "components/DesignSystem/Modal/Modal";
import { TextInput } from "components/DesignSystem/TextInput/TextInput";
import { PriceInput } from "components/PriceInput/PriceInput";
import {
  accountTypes,
  BANK_BRANDS_SEARCH_MIN_LENGTH,
  BANK_CONNECTED_BROADCAST_MESSAGE,
  BANK_CONNECTION_SUCCESS_BROADCAST_CHANNEL,
} from "constants/connections";
import { YYYY_MM_DD } from "constants/date";
import dayjs from "dayjs";
import {
  Field,
  FieldProps,
  Form,
  Formik,
  FormikHelpers,
  useFormikContext,
} from "formik";
import { useMemo, useState } from "react";
import { OnChangeValue } from "react-select";
import { addManualBankSchema } from "formValidations/addManualBankSchema";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useModal } from "hooks/useModal";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { useToast } from "hooks/useToast";
import { useHistory } from "react-router-dom";
import { useGetCurrenciesQuery } from "store/apis/invoices";
import {
  useCreateManualBankAccountMutation,
  useGetInfiniteBankBrandsInfiniteQuery,
} from "store/apis/booksConnections";
import { BackendError } from "types/utils/error";
import { AddBankSuccessModal } from "./AddBankSuccessModal";
import { ModalProps } from "types/utils/modal";
import { ManualBank } from "types/Models/banks";
import { useBroadcastChannel } from "hooks/useBroadcastChannel";
import { debounce } from "utils/debouncing";

type AddManualBankValues = {
  bank_name_id: string;
  account_number: string;
  account_type: string;
  opening_balance: number;
  account_opening_date: string;
  currency: string;
};

const AccountName = () => {
  const {
    setFieldValue,
    values: { bank_name_id },
  } = useFormikContext<AddManualBankValues>();
  const { uuid: groupId } = useCurrentGroupContext();
  const { isAdmin } = useRoleBasedView();

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedBrand, setSelectedBrand] = useState<OptionData | null>(null);

  const { data, isFetching, fetchNextPage, hasNextPage } =
    useGetInfiniteBankBrandsInfiniteQuery(
      { groupId, searchTerm },
      { skip: !groupId }
    );

  const onSearchTermChange = useMemo(
    () =>
      debounce((value: string) => {
        const trimmedValue = value.trim();
        const isSearchable =
          trimmedValue.length >= BANK_BRANDS_SEARCH_MIN_LENGTH;
        setSearchTerm(isSearchable ? trimmedValue : "");
      }, 300),
    []
  );

  const bankBrandsOptions = data?.pages
    .flatMap(({ bank_brands }) => bank_brands)
    .map(({ name, uuid }) => ({ value: uuid, label: name }));

  const created = bank_name_id
    ? { value: bank_name_id, label: bank_name_id }
    : null;

  const selected =
    bankBrandsOptions?.find(({ value }) => value === bank_name_id) ||
    (selectedBrand?.value === bank_name_id ? selectedBrand : created);

  const handleChange = (option: OnChangeValue<OptionData, boolean>) => {
    if (option instanceof Array) return;
    setSelectedBrand(option);
  };

  const handleCreateOption = (name: string) => {
    setFieldValue("bank_name_id", name);
  };

  const loadNextBankBrands = () => {
    if (hasNextPage && !isFetching) {
      void fetchNextPage();
    }
  };

  return (
    <Combobox
      withForm
      required
      creatable={isAdmin}
      name="bank_name_id"
      label="Account name"
      placeholder="Select or type"
      options={bankBrandsOptions}
      menuPortalTarget={document.body}
      value={selected}
      isLoading={isFetching}
      onChange={handleChange}
      onCreateOption={isAdmin && handleCreateOption}
      onInputChange={onSearchTermChange}
      onMenuScrollToBottom={loadNextBankBrands}
    />
  );
};

const CurrencySelector = () => {
  const { values } = useFormikContext<AddManualBankValues>();
  const { data: currenciesResponse } = useGetCurrenciesQuery();

  const currencyOptions = useMemo(() => {
    if (!currenciesResponse) return [];
    return Object.entries(currenciesResponse).map(([code, name]) => ({
      label: `${code} - ${name}`,
      value: code,
    }));
  }, [currenciesResponse]);

  return (
    <Combobox
      withForm
      required
      name="currency"
      label="Select account currency"
      options={currencyOptions}
      value={currencyOptions.find((val) => val.value === values.currency)}
      placeholder="Select a currency"
      menuPortalTarget={document.body}
    />
  );
};

export const AddManualBank = ({
  isOpen,
  close,
  onSuccess,
  entityId,
}: ModalProps & {
  onSuccess?: (bank: ManualBank & { entityId: string }) => void;
  entityId: string;
}) => {
  const { alertToast, successToast } = useToast();
  const { isCustomer } = useRoleBasedView();
  const { uuid: groupId } = useCurrentGroupContext();
  const { postMessage } = useBroadcastChannel(
    BANK_CONNECTION_SUCCESS_BROADCAST_CHANNEL
  );

  const [createManualBankAccount, { data: addedBank }] =
    useCreateManualBankAccountMutation();

  const { entity_bank_account_id } = addedBank || {};

  const {
    push,
    location: { search },
  } = useHistory();

  const {
    isOpen: isSuccessModalOpen,
    close: closeSuccessModal,
    open: openSuccessModal,
  } = useModal();

  const openChat = () => {
    close();
    push(`/books/chat${search}`);
  };

  const onSubmit = async (
    values: AddManualBankValues,
    { resetForm }: FormikHelpers<AddManualBankValues>
  ) => {
    try {
      const bank = await createManualBankAccount({
        ...values,
        account_opening_date: dayjs(values.account_opening_date).format(
          YYYY_MM_DD
        ),
        opening_balance: values.opening_balance.toString(),
        entityId,
        groupId,
      }).unwrap();

      successToast({
        message: "Bank added successfully!",
      });
      postMessage(BANK_CONNECTED_BROADCAST_MESSAGE);
      resetForm();
      close();
      onSuccess?.({ ...bank, entityId });
      openSuccessModal();
    } catch (error) {
      alertToast({
        message: (error as BackendError)?.data?.error?.message,
      });
    }
  };

  return (
    <>
      <Modal.Root open={isOpen} onOpenChange={close} modal={false}>
        <Formik
          initialValues={{
            bank_name_id: "",
            account_number: "",
            account_type: "",
            opening_balance: 0,
            account_opening_date: "",
            currency: "USD",
          }}
          onSubmit={onSubmit}
          enableReinitialize
          validateOnChange
          validationSchema={addManualBankSchema}
        >
          {({ submitForm, isSubmitting }) => (
            <Form>
              <Modal.Content useCustomOverlay>
                <Modal.Header className="sm:t-pl-4">
                  <Modal.Title>Add a Bank/Card</Modal.Title>
                  <Modal.Close />
                </Modal.Header>
                <Modal.Body className="t-flex t-gap-4 t-flex-col sm:t-px-4">
                  <AccountName />
                  <CurrencySelector />
                  <TextInput
                    required
                    name="account_number"
                    label="Account number"
                  />
                  <Combobox
                    withForm
                    required
                    name="account_type"
                    label="Account type"
                    options={accountTypes}
                    menuPortalTarget={document.body}
                  />
                  <PriceInput
                    required
                    name="opening_balance"
                    label="Opening balance"
                  />
                  <Field name="account_opening_date">
                    {({ field }: FieldProps) => {
                      return (
                        <DateInput
                          {...field}
                          required
                          portalId="add-manual-bank-date"
                          maxDate={new Date()}
                          label="Date of opening balance"
                        />
                      );
                    }}
                  </Field>
                </Modal.Body>
                <Modal.Footer className="t-flex t-justify-between t-items-center sm:t-gap-3 sm:t-px-4">
                  {isCustomer && (
                    <div className="t-flex t-gap-1 t-w-full t-items-center sm:t-w-auto sm:t-shrink-0 sm:t-flex-col sm:t-items-start sm:t-gap-0 sm:t-text-body-sm">
                      Need help?
                      <Button
                        type="button"
                        customType="link"
                        onClick={openChat}
                      >
                        Chat with us.
                      </Button>
                    </div>
                  )}
                  <div className="t-flex t-gap-3 t-justify-end t-w-full">
                    <Button
                      type="reset"
                      onClick={close}
                      disabled={isSubmitting}
                    >
                      Cancel
                    </Button>
                    <Button
                      customType="primary"
                      onClick={submitForm}
                      isLoading={isSubmitting}
                      disabled={isSubmitting}
                      type="submit"
                    >
                      Add
                    </Button>
                  </div>
                </Modal.Footer>
              </Modal.Content>
            </Form>
          )}
        </Formik>
      </Modal.Root>
      <AddBankSuccessModal
        isOpen={isSuccessModalOpen}
        close={closeSuccessModal}
        accountId={entity_bank_account_id!}
      />
    </>
  );
};
